import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, Stack, SxProps, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Table from '@/components/ui/table';
import Pagination from '@/components/ui/pagination';
import { PaginationMeta } from '@/shared/types/pagination';
import { PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import DashboardItem from '@/features/dashboard/DashboardItem';
import { UseQueryResult } from '@tanstack/react-query';
import { Column } from '@/components/ui/table/types';
import { Theme } from 'node_modules/@emotion/react/dist/declarations/src';
import Backdrop from '@/components/ui/backdrop';

type StatisticsTableProps<T> = {
	title: string;
	query: UseQueryResult<Array<T>, unknown>;
	columns: Array<Column<T>>;
	exportLabel?: string;
	onExport?: () => void;
	emptyState: ReactNode;
	dataTransform?: (items: T[]) => T[];
	showViewButtons?: boolean;
	onViewChange?: (view: 'completed' | 'error') => void;
	viewMode?: 'completed' | 'error';
	splitColumns?: boolean;
	itemsPerPage?: number;
	sx?: SxProps<Theme>;
	isLoading?: boolean;
};

const StatisticsTable = <DataModel extends object>({
	title,
	query,
	columns,
	emptyState,
	dataTransform,
	showViewButtons,
	onViewChange,
	viewMode,
	splitColumns = false,
	itemsPerPage = 5,
	sx,
	isLoading = false,
}: StatisticsTableProps<DataModel>): ReactElement => {
	const [entities, setEntities] = useState<DataModel[]>([]);
	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
		...PAGINATION_META_DEFAULT,
		pageSize: itemsPerPage,
	});
	const { currentPage, totalItems, pageSize } = paginationMeta;

	const { data } = query;

	useEffect(() => {
		if (data) {
			const transformed = dataTransform ? dataTransform(data) : data;
			setEntities(transformed);
			setPaginationMeta((prev) => ({
				...prev,
				totalItems: transformed.length,
			}));
		}
	}, [data, dataTransform]);

	const dataToDisplay = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		return entities.slice(startIndex, startIndex + itemsPerPage);
	}, [currentPage, entities, itemsPerPage]);

	const currentPageRows = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;

		return {
			start: startIndex + 1,
			end: endIndex > totalItems ? totalItems : endIndex,
		};
	}, [totalItems, currentPage, itemsPerPage]);

	const splitData = useMemo(() => {
		if (!splitColumns) return [dataToDisplay];
		const midIndex = Math.ceil(dataToDisplay.length / 2);
		return [dataToDisplay.slice(0, midIndex), dataToDisplay.slice(midIndex)];
	}, [dataToDisplay, splitColumns]);

	return (
		<DashboardItem title={title} sx={sx}>
			<Box sx={{ position: 'relative' }}>
				{isLoading && (
					<Backdrop
						sx={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'rgba(255, 255, 255, 1)',
						}}
					/>
				)}
				<Stack direction="column" spacing={2.5}>
					{showViewButtons && onViewChange && (
						<Stack direction="row" spacing={2} sx={{ displayPrint: 'none' }}>
							<ToggleButtonGroup
								value={viewMode}
								color="primary"
								exclusive
								onChange={(_: React.MouseEvent<HTMLElement>, value: 'completed' | 'error') => {
									if (value) onViewChange(value);
								}}
								aria-label="statistics view mode"
							>
								<ToggleButton value="completed" aria-label="Most Active">
									Most Active
								</ToggleButton>
								<ToggleButton value="error" aria-label="Highest Error Rate">
									Highest Error Rate
								</ToggleButton>
							</ToggleButtonGroup>
						</Stack>
					)}
					<Stack
						direction="row"
						spacing={2}
						sx={{
							display: 'none',
							displayPrint: 'flex',
							justifyContent: 'space-between',
						}}
					>
						<TextField
							label="Rows"
							size="small"
							value={`${currentPageRows.start} - ${currentPageRows.end} of ${totalItems}`}
						/>
					</Stack>
					<>
						{splitData.length > 1 ? (
							<Stack direction="row" spacing={2}>
								{splitData.map((dataChunk, index) => (
									<Table<DataModel>
										key={index}
										ariaLabel={`${title} - Column ${index + 1}`}
										empty={emptyState}
										data={dataChunk}
										columns={columns}
									/>
								))}
							</Stack>
						) : (
							splitData.map((dataChunk, index) => (
								<Table<DataModel>
									key={index}
									ariaLabel={`${title} - Column ${index + 1}`}
									empty={emptyState}
									data={dataChunk}
									columns={columns}
								/>
							))
						)}
						<Pagination
							hideOnPrint
							currentPage={currentPage}
							perPage={pageSize}
							totalItems={totalItems}
							onPageChange={(page) => {
								setPaginationMeta({ ...paginationMeta, currentPage: page });
							}}
						/>
					</>
				</Stack>
			</Box>
		</DashboardItem>
	);
};

export default StatisticsTable;
